import { Component, EventTouch, macro, Node, Sprite, SpriteFrame, TransformBit, v2, view, _decorator } from 'cc';
import { DEV } from 'cc/env';
import Page from '../../../../common/scripts/Toolkit/Components/Page/Page';

const { ccclass, property, requireComponent } = _decorator;
const tempVec2A = v2();
const tempVec2B = v2();

@ccclass('GameRuleImageViewer')
@requireComponent(Page)
export default class GameRuleImageViewer extends Component {
    @property(Node)
    private readonly imageContainer: Node = null;

    @property(Sprite)
    private readonly image: Sprite = null;

    @property({ tooltip: DEV && '兩指距離每增加/减少多少 pixel，要放大/缩小 1 倍' })
    private readonly scaleRatio: number = 100;

    @property({ tooltip: DEV && '最大缩放倍数' })
    private readonly maxScale: number = 2;

    @property({ tooltip: DEV && '最小缩放倍数' })
    private readonly minScale: number = 1;

    private _page: Page = null;

    private _isTouching: boolean = false;
    private _isPinching: boolean = false;
    private _startPinchDistance: number = 0;
    private _startPinchScale: number = 0;

    private get page(): Page {
        if (!this._page) {
            this._page = this.getComponent(Page);
        }
        return this._page;
    }

    private get isTouching(): boolean {
        return this._isTouching;
    }
    private set isTouching(value: boolean) {
        if (this._isTouching === value) {
            return;
        }
        this._isTouching = value;
        this.page.closeButton.interactable = !value;
    }

    public onLoad(): void {
        this.page.on(Page.EVENT_TYPE.OPEN_START, () => {
            macro.ENABLE_MULTI_TOUCH = true;
        });
        // reset after page close
        this.page.on(Page.EVENT_TYPE.CLOSE_END, () => {
            macro.ENABLE_MULTI_TOUCH = false;
            this.image.node.scaleScalar = 1;
            this._isPinching = false;
            this._startPinchDistance = 0;
            this._startPinchScale = 1;
        });
        // update container size when image size or scale changed
        this.image.node.on(Node.EventType.SIZE_CHANGED, this._updateContainerSize, this);
        this.image.node.on(
            Node.EventType.TRANSFORM_CHANGED,
            (transformBit: TransformBit) => {
                if ((transformBit as number) & TransformBit.SCALE) {
                    this._updateContainerSize();
                }
            },
            this,
        );

        this._registerPinchEventListener();
    }

    public show(spriteFrame: SpriteFrame): void {
        this.image.spriteFrame = spriteFrame;
        // use height because the image is rotated
        const targetWidth = view.getDesignResolutionSize().height;
        const scale = targetWidth / spriteFrame.originalSize.width;
        this.image.node.transform.setContentSize(targetWidth, spriteFrame.originalSize.height * scale);
        this.page.open();
    }

    private _registerPinchEventListener() {
        // start pinching when touch count is more than 2
        this.image.on(Node.EventType.TOUCH_START, (event: EventTouch) => {
            if (event.getAllTouches().length >= 1) {
                this.isTouching = true;
            }
            if (event.getAllTouches().length <= 1) {
                return;
            }
            if (!this._isPinching) {
                this._isPinching = true;
                const [touchOne, touchTwo] = event.getAllTouches();
                const locationOne = touchOne.getLocation(tempVec2A);
                const locationTwo = touchTwo.getLocation(tempVec2B);
                this._startPinchDistance = locationOne.subtract(locationTwo).length();
                this._startPinchScale = this.image.node.scaleScalar;
            }
        });

        // pinch to zoom
        this.image.on(Node.EventType.TOUCH_MOVE, (event: EventTouch) => {
            if (!this._isPinching) {
                return;
            }
            const [touchOne, touchTwo] = event.getAllTouches();
            const locationOne = touchOne.getLocation(tempVec2A);
            const locationTwo = touchTwo.getLocation(tempVec2B);
            const currentDistance = locationOne.subtract(locationTwo).length();
            const scale = Math.max(
                this.minScale,
                Math.min(this.maxScale, this._startPinchScale + (currentDistance - this._startPinchDistance) / this.scaleRatio),
            );
            this.image.node.scaleScalar = scale;
        });

        // stop pinching when touch count is less than 2
        this.image.on(Node.EventType.TOUCH_END, (event: EventTouch) => {
            if (this.isTouching && event.getAllTouches().length === 0) {
                this.isTouching = false;
            }
            if (this._isPinching && event.getAllTouches().length <= 1) {
                this._isPinching = false;
            }
        });
        this.image.on(Node.EventType.TOUCH_CANCEL, (event: EventTouch) => {
            if (this.isTouching && event.getAllTouches().length === 0) {
                this.isTouching = false;
            }
            if (this._isPinching && event.getAllTouches().length <= 1) {
                this._isPinching = false;
            }
        });
    }

    private _updateContainerSize() {
        const imageSize = this.image.node.transform.contentSize;
        const scale = this.image.node.scaleScalar;
        const width = imageSize.width * scale;
        const height = imageSize.height * scale;
        // because the image is rotated, we need to swap width and height
        this.imageContainer.transform.setContentSize(height, width);
    }
}
